import { defaultPropertyDefinitions } from "@buildwithflux/constants";
import { ElementHelper } from "../../../elements";
import { filterTruthy } from "../../../helpers/arrays";
import { getOrderedNetPins } from "../../models";
import { BUS_TYPE_TO_BUS_MAP, PART_TYPE_TO_BUS_MAP } from "./partMappings";
// We need to distill the more abstract properties down into the following properties
const COMPUTED_PROPERTIES = [
    defaultPropertyDefinitions.current,
    defaultPropertyDefinitions.desiredTemperatureRise,
    defaultPropertyDefinitions.controlled_impedance,
    defaultPropertyDefinitions.controlled_impedance_tolerance,
    defaultPropertyDefinitions.controlled_impedance_pair,
    defaultPropertyDefinitions.positive_to_negative_skew_max,
    defaultPropertyDefinitions.pin_delay,
    defaultPropertyDefinitions.bus_group,
    defaultPropertyDefinitions.pair_role,
    defaultPropertyDefinitions.bus_type,
].reduce((acc, property) => {
    acc[property.key] = property.label;
    return acc;
}, {});
function getPropertyByName(properties, propertyName) {
    return Object.values(properties).find((property) => property.name === propertyName);
}
/**
 * Computes the impedance properties for a given net and adds them to the net.
 * this is designed to be called after a net is created or modified. We need to
 * recalculate the impedance properties for the net each time a change is made due
 * as the new pins may redefine the property values.
 *
 * Since multiple pins can have different values, it returns an array. It's up to the data consumer to chose the best one.
 * In case there is a clear override (like with a prop on a net), that one will be used instead.
 */
export function getComputedPropertiesForNet(net, elements) {
    const computedProperties = {};
    const pins = getOrderedNetPins({ [net.uid]: net }, net.uid);
    for (const [propertyKey, propertyName] of Object.entries(COMPUTED_PROPERTIES)) {
        const propertyValue = findPropertyValueForNet(net, pins, elements, propertyName, propertyKey);
        if (propertyValue) {
            computedProperties[propertyKey] = propertyValue;
        }
    }
    return computedProperties;
}
function findPropertyValueForNet(net, orderedPins, elements, propertyName, propertyKey) {
    // Check if the property is already defined on the net with a value
    // (ignore empty preset properties so we can fall back to computed values from terminals)
    const netProperty = getPropertyByName(net.properties, propertyName);
    if (netProperty && netProperty.value) {
        return [netProperty];
    }
    // If we don't have a net property defined, we need to look at each pin
    // in the net and see if we can find a property that matches the name
    const pinsProperties = filterTruthy(orderedPins.map((pin) => {
        // See if the property is directly defined on the pin
        const element = elements[pin.elementUid];
        if (!element)
            return undefined;
        const terminal = ElementHelper.getTerminalWithProperties(element, pin.terminalUid);
        if (!terminal)
            return undefined;
        const terminalName = terminal.name;
        const elementDesignator = element.label;
        return findPropertyForPin(pin, terminalName, elementDesignator, net, terminal.properties, element.properties, propertyName, propertyKey);
    }));
    return pinsProperties.length > 0 ? pinsProperties : undefined;
}
function findPropertyForPin(pin, terminalName, elementDesignator, net, terminalProperties, elementProperties, propertyName, propertyKey) {
    if (terminalProperties) {
        const terminalProperty = getPropertyByName(terminalProperties, propertyName);
        if (terminalProperty) {
            // QUESTION: is this right?  parentUid is element.uid?
            return {
                ...terminalProperty,
                parentUid: pin.elementUid,
                source: {
                    elementUid: pin.elementUid,
                    elementDesignator,
                    terminalUid: pin.terminalUid,
                    terminalName,
                    propertyName,
                },
            };
        }
    }
    // Exit if we don't have a terminal name or element properties
    if (!elementProperties || !terminalName) {
        return undefined;
    }
    // Bus type is more specific than part type, so we need to check that first
    const elementBusType = getPropertyByName(elementProperties, defaultPropertyDefinitions.bus_type.label);
    if (elementBusType) {
        const busTypeMapping = BUS_TYPE_TO_BUS_MAP[elementBusType.value];
        if (busTypeMapping) {
            // Check if the terminal name is in the lookup
            const pinProperties = busTypeMapping.pins[busTypeMapping.pinLookup[terminalName.toLocaleLowerCase()] ?? ""];
            if (pinProperties && propertyKey in pinProperties) {
                return {
                    name: propertyName,
                    uid: `${net.uid}__${propertyKey}`,
                    value: pinProperties[propertyKey],
                    parentUid: pin.elementUid,
                    source: {
                        elementUid: pin.elementUid,
                        elementDesignator,
                        terminalUid: pin.terminalUid,
                        terminalName,
                        propertyName,
                    },
                };
            }
        }
    }
    // Finally, check the part type to see if we can determine the impedance properties
    // based on that and the pin name
    const elementPartType = getPropertyByName(elementProperties, defaultPropertyDefinitions.part_type.label);
    if (!elementPartType) {
        return undefined;
    }
    // See if this part type exists in parts mapping
    const partMapping = PART_TYPE_TO_BUS_MAP[elementPartType.value];
    if (!partMapping) {
        return undefined;
    }
    // Check if the terminal name is in the lookup
    const pinProperties = partMapping.pins[partMapping.pinLookup[terminalName.toLocaleLowerCase()] ?? ""];
    if (!pinProperties) {
        return undefined;
    }
    // Get the impedance properies for this terminal and party type combo
    if (propertyKey in pinProperties) {
        return {
            name: propertyName,
            uid: `${net.uid}__${propertyKey}`,
            value: pinProperties[propertyKey],
            parentUid: pin.elementUid,
            source: {
                elementUid: pin.elementUid,
                elementDesignator,
                terminalUid: pin.terminalUid,
                terminalName,
                propertyName,
            },
        };
    }
    return undefined;
}
export function getAllSmartProperties() {
    return Object.keys(COMPUTED_PROPERTIES);
}
/**
 * Retreives the key for all impedance properties that are defined for a given terminal on an element.  Note that the properties may be defined
 * on either the terminal or the element, depending on the property in question.
 *
 * IMPORTANT NOTE: YOU SHOULD NOT RELY ON THIS FUNCTION TO DETERMINE IF A PROPERTY IS DEFINED OR NOT.  IT IS ONLY INTENDED TO BE USED TO SCORE
 * TERMINALS AND ELEMENTS FOR THE PURPOSES OF SORTING IN SEARCH.
 */
export function getAllDefinedSmartPropertiesForTerminal(terminal, partVersionData) {
    // Fake pin
    const fakePin = {
        uid: "fake",
        elementUid: "fake",
        terminalUid: terminal.uid,
    };
    const terminalName = terminal.name;
    const result = [];
    for (const [propertyKey, propertyName] of Object.entries(COMPUTED_PROPERTIES)) {
        // NOTE: This is a bullshit net that we are creating out of whole cloth in order to extract the property in question.  It obviously
        // isn't meaningful, but it's enough to trick the code into giving us the property we want.
        const fakeNet = {
            uid: "fake",
            hash: "fake",
            pins: {},
            properties: {},
            label: "fake",
            nextPinOrder: 0,
        };
        const propertyValue = findPropertyForPin(fakePin, terminalName, undefined, // No element designator for fake pin
        fakeNet, terminal.properties, partVersionData.properties, propertyName, propertyKey);
        if (propertyValue) {
            result.push(propertyKey);
        }
    }
    return result;
}
//# sourceMappingURL=computedProperties.js.map